/** Presentation of a real server turn. Report figures never come from model prose. */
import type { AiChart, AiEvidence, AiTurn } from '../connect/contract.ts';
import { groupDigits } from '../connect/display.ts';
import type { AnswerMessage, ChartSpec, DeepSearchProgressSpec, TableBlock, TableColumn } from './types.ts';

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  'inventory.stock.summary': 'موجودی انبار',
  'crm.opportunity.open': 'فرصت‌های فروش',
  'procurement.order.open': 'سفارش‌های خرید',
  'accounting.open-items': 'اطلاعات مالی',
  'projects.task.overdue': 'اطلاعات پروژه',
};

const STATUS_LABELS: Readonly<Record<string, string>> = {
  open: 'باز',
  approved: 'تأییدشده',
  partially_received: 'دریافت جزئی',
  todo: 'انجام‌نشده',
  in_progress: 'در حال انجام',
  overdue: 'سررسیدگذشته',
};

function sourceLabel(item: AiEvidence): string {
  return item.group || SOURCE_LABELS[item.source] || item.source;
}

function quantity(value: string): string {
  const [num, den] = value.split('/');
  return den === undefined || den === '1'
    ? groupDigits(num ?? value)
    : `${groupDigits(num ?? '')}/${groupDigits(den)}`;
}

function evidenceValue(item: AiEvidence): string {
  const rial = item.openBalanceRial ?? item.amountRial;
  if (rial !== undefined) return `${groupDigits(rial)} ریال`;
  if (item.daysOverdue !== undefined) return `${groupDigits(String(item.daysOverdue))} روز تأخیر`;
  if (item.onHand !== undefined) return quantity(item.onHand);
  return '—';
}

/** Exact strings remain exact even when an amount exceeds JS's safe integer range. */
export function evidenceTable(evidence: readonly AiEvidence[]): TableBlock | undefined {
  if (evidence.length === 0) return undefined;
  const factsOnly = evidence.every((item) => item.entity === 'fact');
  const columns: TableColumn[] = [
    { key: 'source', label: 'بخش' },
    { key: 'reference', label: 'مرجع' },
    { key: 'label', label: factsOnly ? 'اطلاعات مبنای پاسخ' : 'عنوان' },
  ];
  if (!factsOnly) {
    columns.push({ key: 'value', label: 'مقدار / مبلغ', align: 'num' });
    if (evidence.some((item) => item.status !== undefined))
      columns.push({ key: 'status', label: 'وضعیت' });
    if (evidence.some((item) => item.dueDate))
      columns.push({ key: 'due', label: 'سررسید ثبت‌شده' });
  }
  return {
    caption: 'داده‌های مبنای پاسخ',
    columns,
    rows: evidence.map((item, index) => {
      const isDocument = /^[A-Z]{2,4}-\d+/i.test(item.recordId);
      return {
        id: `${item.source}:${item.recordId}:${index}`,
        cells: {
          source: sourceLabel(item),
          reference: isDocument
            ? { kind: 'record' as const, text: item.recordId }
            : item.recordId,
          label: item.label || '—',
          value: evidenceValue(item),
          status: STATUS_LABELS[item.status ?? ''] ?? item.status ?? '—',
          due: item.dueDate || '—',
        },
      };
    }),
  };
}

/** Plot server data only. An unsafe/clamped figure must never become a financial bar. */
export function liveChart(chart: AiChart | null | undefined, id: string): ChartSpec | undefined {
  if (
    !chart ||
    chart.type === 'metric' ||
    (chart.items.length === 0 && (!chart.categories || chart.categories.length === 0))
  )
    return undefined;
  if (
    chart.items.some(
      (item) => !Number.isFinite(item.value) || Math.abs(item.value) >= Number.MAX_SAFE_INTEGER,
    )
  )
    return undefined;
  const units = new Set(chart.items.map((item) => item.unit ?? chart.unit ?? ''));
  if (units.size > 1) return undefined;
  const unit = chart.unit ?? chart.items[0]?.unit ?? '';
  const data = chart.items.map((item) => ({ label: item.label, value: item.value }));
  if (
    (chart.type === 'pie' || chart.type === 'donut') &&
    (data.some((item) => item.value < 0) || data.every((item) => item.value === 0))
  )
    return undefined;
  // Older report titles named Rial while the bars were scaled to thousand Rial.
  // The item unit describes the actual values; replace a conflicting suffix.
  const title =
    unit && !chart.title.includes(unit)
      ? chart.title.replace(/\s*\((?:(?:هزار|میلیون|میلیارد) )?(?:ریال|تومان)\)\s*$/, '')
      : chart.title;
  const caption = unit && !title.includes(unit) ? `${title} (${unit})` : title;

  if (chart.type === 'line') {
    const categories =
      chart.categories && chart.categories.length > 0
        ? chart.categories
        : chart.items.map((item) => item.label);
    const series =
      chart.series && chart.series.length > 0
        ? chart.series
        : [
            {
              name: caption,
              data: chart.items.map((item) => item.value),
            },
          ];
    return {
      id: `server-chart-${id}`,
      kind: 'line',
      caption,
      ariaLabel: caption,
      valueSuffix: unit ? ` ${unit}` : '',
      categories,
      series,
    };
  }

  if (chart.type === 'pie' || chart.type === 'donut') {
    let pieData = data;
    if (data.length > 5) {
      const top5 = data.slice(0, 5);
      const otherTotal = data.slice(5).reduce((sum, item) => sum + item.value, 0);
      pieData = otherTotal > 0 ? [...top5, { label: 'سایر', value: otherTotal }] : top5;
    }
    return {
      id: `server-chart-${id}`,
      kind: 'donut',
      caption,
      ariaLabel: caption,
      valueSuffix: unit ? ` ${unit}` : '',
      centerLabel: 'مجموع',
      data: pieData,
    };
  }

  return {
    id: `server-chart-${id}`,
    kind: 'bar',
    caption,
    ariaLabel: caption,
    valueSuffix: unit ? ` ${unit}` : '',
    data,
  };
}

export function liveTurnAnswer(id: string, turn: AiTurn): AnswerMessage {
  const evidence = turn.evidence;
  const hasEvidence = evidence.length > 0;
  const sources = [...new Set(evidence.map(sourceLabel))];
  const extracted = [...new Set(evidence.map((item) => item.extractedAt))];
  const chart = hasEvidence ? liveChart(turn.chart, id) : undefined;
  const table = evidenceTable(evidence);
  const kpis = hasEvidence
    ? turn.kpis?.map((item, index) => ({
        id: item.id ?? `server-kpi-${index}`,
        label: item.label,
        value: item.value,
        ...([item.hint, item.unit].filter(Boolean).length
          ? { hint: [item.hint, item.unit].filter(Boolean).join('؛ ') }
          : {}),
      }))
    : undefined;
  const sourceLines = hasEvidence
    ? [
        `منابع مجاز: ${sources.join('، ')}`,
        `پوشش جدول: ${groupDigits(String(evidence.length))} رکورد یا گزارهٔ برگشتی؛ این تعداد، کل اسناد شرکت نیست.`,
        ...extracted.map((stamp) => `زمان استخراج (UTC): ${stamp}`),
        ...(evidence.some(
          (item) => item.source === 'crm.opportunity.open' && item.entity !== 'fact',
        )
          ? ['ارزش فرصت‌های فروش، فروش تحقق‌یافته نیست.']
          : []),
        ...(evidence.some(
          (item) => item.source === 'accounting.open-items' && item.entity !== 'fact',
        )
          ? ['مطالبات و بدهی‌ها جدا هستند و با یکدیگر تهاتر نشده‌اند.']
          : []),
        ...(turn.chart && !chart
          ? ['نمودار این پاسخ به دلیل نوع یا مقادیر غیرقابل‌نمایش ارائه نشد؛ جدول مبنای بررسی است.']
          : []),
        ...(turn.deepSearch?.sources?.some((s) => s.kind === 'web')
          ? [
              `منابع وب مورد استناد: ${turn.deepSearch.sources
                .filter((s) => s.kind === 'web')
                .map((s) => s.title)
                .join(' | ')}`,
            ]
          : []),
        ...(turn.deepSearch?.contradictions?.length
          ? [`تناقض‌های شناسایی‌شده میان منابع: ${turn.deepSearch.contradictions.join('؛ ')}`]
          : []),
      ]
    : [];

  const deepSearchSpec: DeepSearchProgressSpec | undefined = turn.deepSearch
    ? {
        planId: turn.deepSearch.plan?.subQueries?.[0] ? 'deep-plan' : undefined,
        scope: 'hybrid',
        subQueries: turn.deepSearch.plan?.subQueries ?? [],
        steps: turn.deepSearch.plan?.steps ?? [],
        webSources: turn.deepSearch.sources
          ?.filter((s) => s.kind === 'web')
          .map((s) => ({
            title: s.title,
            url: s.url ?? '',
            snippet: s.snippet,
            accessedAt: s.accessedAt,
            publishedAt: s.publishedAt,
          })),
        contradictions: turn.deepSearch.contradictions,
      }
    : undefined;

  const inTokens =
    turn.usage?.inputTokens !== null && turn.usage?.inputTokens !== undefined
      ? BigInt(turn.usage.inputTokens)
      : null;
  const outTokens =
    turn.usage?.outputTokens !== null && turn.usage?.outputTokens !== undefined
      ? BigInt(turn.usage.outputTokens)
      : null;

  let computedTokens = 0;
  let usageLabel: string;

  if (turn.state === 'pending') {
    usageLabel = 'مصرف در انتظار تعیین وضعیت';
  } else if (inTokens === null || outTokens === null) {
    usageLabel = 'مصرف نامشخص';
  } else {
    const rawTotal = inTokens + outTokens;
    computedTokens = Number(rawTotal);
    if (rawTotal === 0n) {
      usageLabel = 'بدون مصرف توکن';
    } else {
      usageLabel = `مصرف ${groupDigits(String(rawTotal))} توکن`;
    }
  }

  return {
    id,
    role: 'assistant',
    kind: 'answer',
    sampleId: 'server',
    isSample: false,
    content: {
      text: [turn.answer ?? turn.stateLabel],
      ...(kpis?.length ? { kpis } : {}),
      ...(chart ? { chart } : {}),
      ...(table ? { table } : {}),
      ...(sourceLines.length ? { source: { lines: sourceLines, postedOnly: false } } : {}),
      ...(deepSearchSpec ? { deepSearch: deepSearchSpec } : {}),
    },
    footer: {
      tokens: computedTokens,
      unit: 'توکن',
      sample: false,
      usageLabel,
      basis: hasEvidence
        ? 'بر اساس اطلاعات مجاز ثبت‌شدهٔ شرکت'
        : 'بدون استناد به دادهٔ عملیاتی شرکت',
    },
    context: {
      path: [
        {
          id: `server-${turn.domain}`,
          label: hasEvidence ? 'تحلیل شرکت' : 'گفتگو',
          kind: 'topic',
          messageId: id,
        },
      ],
      filters: sources,
      sources,
    },
  };
}
